import { MORTGAGE_LIMITS } from "shared/CalculatorLimits";
import { useMortgageRatesQuery } from "web_myplann/Api/Calculators/useMortgageRatesQuery";
import { CalculatorRateAmount } from "web_myplann/Calculators/Components/CalculatorRateAmount";
import { CalculatorRateComparison } from "web_myplann/Calculators/Components/CalculatorRateComparison";
import { CalculatorRatesNote } from "web_myplann/Calculators/Components/CalculatorRatesNote";
import { CalculatorResultSection } from "web_myplann/Calculators/Components/CalculatorResultSection";
import { CalculatorShell } from "web_myplann/Calculators/Components/CalculatorShell";
import {
	MORTGAGE_DEFAULT_LOAN_VALUE,
	MORTGAGE_DEFAULT_PROPERTY_PRICE,
	MORTGAGE_DEFAULT_YEARS,
} from "web_myplann/Calculators/constants";
import { getMortgageFormSchema } from "web_myplann/Calculators/Forms/mortgageFormSchema";
import { useCalculatorForm } from "web_myplann/Calculators/Hooks/useCalculatorForm";
import { useCalculatorLead } from "web_myplann/Calculators/Hooks/useCalculatorLead";
import { useYearsLabel } from "web_myplann/Calculators/Hooks/useYearsLabel";
import { CalculatorType } from "web_myplann/Calculators/Types/CalculatorType";
import { getMortgagePlan } from "web_myplann/Calculators/Utils/getMortgagePlan";
import { mapBankRateBars } from "web_myplann/Calculators/Utils/mapBankRateBars";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import {
	formatCurrency,
	getCurrencySymbol,
} from "web_myplann/Infrastructure/Utils/CurrencyUtils";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { SliderFormRow } from "web_myplann/Shared/Components/SliderFormRow";
import { SliderRow } from "web_myplann/Shared/Components/SliderRow";
import { CurrencyCode } from "web_myplann/Shared/constants";
import { formatPercentage } from "web_myplann/Shared/Utils/percentageFormatters";

export const MortgageCalculatorPage = () => {
	const { t } = useResource();
	const yearsLabel = useYearsLabel();
	const {
		data: rates = [],
		isPending: isRatesPending,
		isError: isRatesError,
	} = useMortgageRatesQuery();
	const hasRates = !isRatesError && rates.length > 0;
	const { send, ...lead } = useCalculatorLead(CalculatorType.Mortgage);
	const { control, watch, setValue, handleSubmit } = useCalculatorForm(
		getMortgageFormSchema,
		{
			propertyPrice: MORTGAGE_DEFAULT_PROPERTY_PRICE,
			years: MORTGAGE_DEFAULT_YEARS,
			loanValue: MORTGAGE_DEFAULT_LOAN_VALUE,
		},
	);

	const { propertyPrice, years, loanValue } = watch();
	const {
		actualLoanValue,
		maxLoanValue,
		ltv,
		bankRates,
		rate,
		monthlyPayment,
	} = getMortgagePlan({ propertyPrice, loanValue, years, rates });
	const hasValidPrice =
		propertyPrice >= MORTGAGE_LIMITS.propertyPriceMin &&
		propertyPrice <= MORTGAGE_LIMITS.propertyPriceMax;
	const hasResult = hasValidPrice && monthlyPayment > 0 && rate !== undefined;
	const isResultLoading = hasValidPrice && isRatesPending;
	const rateBars = mapBankRateBars(bankRates, {
		bankLabel: (index) => t(Resources.Calculators.Result.BankLabel, { index }),
		bankRangeLabel: (from, to) =>
			t(Resources.Calculators.Result.BankRangeLabel, { from, to }),
		notOffered: t(Resources.Calculators.Result.NotOffered),
	});

	const onSubmit = handleSubmit((values) => {
		if (rate === undefined) {
			return;
		}

		send({
			amount: actualLoanValue,
			installment: monthlyPayment,
			interest: rate,
			period: values.years,
			propertyValue: values.propertyPrice,
		});
	});

	return (
		<CalculatorShell
			type={CalculatorType.Mortgage}
			onSubmit={onSubmit}
			fields={
				<>
					<FlatRowFormInput
						control={control}
						name="propertyPrice"
						label={t(Resources.Calculators.Mortgage.PropertyPrice)}
						type="number"
						max={MORTGAGE_LIMITS.propertyPriceMax}
						suffix={getCurrencySymbol(CurrencyCode.CZK)}
					/>
					<SliderFormRow
						control={control}
						name="years"
						label={t(Resources.Calculators.Mortgage.Years)}
						formatValue={yearsLabel}
						min={MORTGAGE_LIMITS.yearsMin}
						max={MORTGAGE_LIMITS.yearsMax}
						disabled={!hasValidPrice}
					/>
					<SliderRow
						label={t(Resources.Calculators.Mortgage.LoanValue)}
						valueLabel={formatCurrency(actualLoanValue, CurrencyCode.CZK, 0)}
						value={actualLoanValue}
						onChange={(value) => setValue("loanValue", value)}
						min={0}
						max={Math.max(maxLoanValue, MORTGAGE_LIMITS.loanStep)}
						step={MORTGAGE_LIMITS.loanStep}
						minLabel={formatCurrency(0, CurrencyCode.CZK, 0)}
						maxLabel={formatCurrency(maxLoanValue, CurrencyCode.CZK, 0)}
						disabled={!hasValidPrice}
						isLast
					/>
				</>
			}
			isResultLoading={isResultLoading}
			result={
				hasResult && (
					<CalculatorResultSection
						badge={t(Resources.Calculators.Result.Badges.monthlyInstalment)}
						hero={<CalculatorRateAmount amount={monthlyPayment} rate={rate} />}
						rows={[
							{
								label: t(Resources.Calculators.Mortgage.ResultLoanValue),
								value: formatCurrency(actualLoanValue, CurrencyCode.CZK, 0),
							},
							{
								label: t(Resources.Calculators.Mortgage.ResultLtv),
								value: formatPercentage(ltv, 0),
							},
						]}
						comparison={
							rateBars.length > 0 && (
								<CalculatorRateComparison
									bars={rateBars}
									intro={t(Resources.Calculators.Result.CompareIntro)}
									variant="partners"
								/>
							)
						}
						lead={lead}
					/>
				)
			}
		>
			{hasValidPrice && !isRatesPending && (
				<CalculatorRatesNote
					hasRates={hasRates}
					isOfferMissing={!hasResult && actualLoanValue > 0}
				/>
			)}
		</CalculatorShell>
	);
};
